import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { SiteFooter } from '../components/SiteFooter';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { WorldSection } from '../components/WorldSection';
import { RealmPortals } from '../components/RealmPortals';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../lib/api';

const COMMISSION_STEPS = [
  {
    number: '01',
    title: 'Request Private Preview',
    description: 'Share your collection interest, occasion, and timeline.',
  },
  {
    number: '02',
    title: 'Review Quote & Design',
    description: 'Final specifications, pricing, stone mapping, and production timeline are confirmed before production begins.',
  },
  {
    number: '03',
    title: 'Enter Production',
    description: 'Each piece is made to order with care, intention, and documented approval.',
  },
  {
    number: '04',
    title: 'Receive The Artifact',
    description: 'Your piece is delivered with refined presentation, care guidance, and house documentation.',
  },
];

const BUDGET_OPTIONS = [
  '$1,000 – $2,000',
  '$2,000 – $4,000',
  '$4,000 – $7,500',
  '$7,500 – $12,000',
  '$12,000+',
  'Not sure yet',
];

const LandingPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: '',
    timeline: '',
    budgetComfort: '',
    message: '',
    founderAccess: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  // Prefill Interest when arriving from a realm or product page
  useEffect(() => {
    const interest = location.state?.interest;
    if (interest) setFormData((prev) => ({ ...prev, interest }));
  }, [location.state]);

  // Support deep links like /#contact from other pages
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitInquiry(formData);
      toast({
        title: 'Preview Request Received',
        description: 'The house will respond personally to your inquiry.',
      });
      setFormData({
        name: '',
        email: '',
        interest: '',
        timeline: '',
        budgetComfort: '',
        message: '',
        founderAccess: false,
      });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again, or email the house directly.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="luxury-landing">
      <SiteNav />

      <Hero onDescend={() => scrollToSection('world')} />

      <WorldSection />

      <RealmPortals />

      {/* Commission Process — clean mobile cards, trust-building */}
      <section id="commission" className="py-20 md:py-28 px-5 md:px-6 bg-gradient-to-b from-[#080A0D] to-[#0B0F14]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-[#C8A45D] text-[10px] md:text-xs tracking-[0.35em] mb-3">
              THE PROCESS
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#F3E8D2] mb-4 leading-tight">
              Made to Order by Private Commission
            </h2>
            <p className="text-[#B9A98A] text-[15px] md:text-base max-w-xl mx-auto leading-relaxed">
              Each piece begins with interest, not inventory. Final specifications, pricing, stone mapping, and production timelines are confirmed before work begins.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {COMMISSION_STEPS.map((step) => (
              <div
                key={step.number}
                className="bg-[#0B0F14] border border-[#C8A45D]/15 p-6 md:p-7 hover:border-[#C8A45D]/35 transition-all duration-500"
              >
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-[#C8A45D] text-2xl md:text-3xl font-light tracking-wider">
                    {step.number}
                  </span>
                  <div className="h-[1px] flex-1 bg-[#C8A45D]/30" />
                </div>
                <h3 className="text-lg md:text-xl font-light text-[#F3E8D2] mb-3 leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#B9A98A] text-[14px] md:text-[15px] leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 md:py-28 px-5 md:px-6 bg-gradient-to-b from-[#080A0D] to-[#0B0F14]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-[#C8A45D] text-[10px] md:text-xs tracking-[0.35em] mb-3">
              INQUIRY
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#F3E8D2] mb-5 leading-tight">
              Request Private Preview
            </h2>
            <p className="text-[#B9A98A] text-[15px] md:text-base leading-relaxed max-w-lg mx-auto">
              Private previews and founding collector access are now opening. Share your interest below and the house will respond personally.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="text-[#B9A98A] text-xs tracking-widest mb-2 block">
                  NAME
                </label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#0B0F14] border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] transition-colors duration-300 h-12"
                  data-testid="inquiry-name-input"
                />
              </div>
              <div>
                <label htmlFor="email" className="text-[#B9A98A] text-xs tracking-widest mb-2 block">
                  EMAIL
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-[#0B0F14] border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] transition-colors duration-300 h-12"
                  data-testid="inquiry-email-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="interest" className="text-[#B9A98A] text-xs tracking-widest mb-2 block">
                INTERESTED COLLECTION OR PIECE
              </label>
              <Input
                id="interest"
                value={formData.interest}
                onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                className="bg-[#0B0F14] border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] transition-colors duration-300 h-12"
                placeholder="e.g., The Sovereign from The Deep"
                data-testid="inquiry-interest-input"
              />
            </div>

            <div>
              <label htmlFor="timeline" className="text-[#B9A98A] text-xs tracking-widest mb-2 block">
                OCCASION OR TIMELINE
              </label>
              <Input
                id="timeline"
                value={formData.timeline}
                onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                className="bg-[#0B0F14] border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] transition-colors duration-300 h-12"
                placeholder="e.g., Anniversary, 6 months"
                data-testid="inquiry-timeline-input"
              />
            </div>

            <div>
              <label htmlFor="budgetComfort" className="text-[#B9A98A] text-xs tracking-widest mb-2 block">
                ESTIMATED COMMISSION COMFORT
              </label>
              <div className="relative">
                <select
                  id="budgetComfort"
                  value={formData.budgetComfort}
                  onChange={(e) => setFormData({ ...formData, budgetComfort: e.target.value })}
                  className="w-full bg-[#0B0F14] border border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] focus:outline-none transition-colors duration-300 h-12 px-3 pr-10 text-sm md:text-base cursor-pointer appearance-none rounded-md"
                  data-testid="inquiry-budget-select"
                >
                  <option value="" className="bg-[#0B0F14] text-[#B9A98A]">Select a range</option>
                  {BUDGET_OPTIONS.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#0B0F14] text-[#F3E8D2]">
                      {opt}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#C8A45D] text-xs">
                  ▾
                </span>
              </div>
              <p className="text-[#8A6A35] text-[11px] italic mt-1.5">
                Helps the house propose pieces within your range. Final pricing confirmed during quote review.
              </p>
            </div>

            <div>
              <label htmlFor="message" className="text-[#B9A98A] text-xs tracking-widest mb-2 block">
                MESSAGE
              </label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-[#0B0F14] border-[#C8A45D]/20 text-[#F3E8D2] focus:border-[#C8A45D] min-h-[120px] transition-colors duration-300"
                placeholder="Share more about your interest..."
                data-testid="inquiry-message-input"
              />
            </div>

            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="founder"
                checked={formData.founderAccess}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, founderAccess: !!checked })
                }
                className="border-[#C8A45D]/40 data-[state=checked]:bg-[#C8A45D] data-[state=checked]:border-[#C8A45D] mt-0.5"
                data-testid="inquiry-founder-checkbox"
              />
              <label htmlFor="founder" className="text-[#B9A98A] text-sm cursor-pointer leading-snug">
                I am interested in founding collector access
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-3">
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#BFA05A] hover:bg-[#C8A45D] text-[#080A0D] py-6 text-base tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#C8A45D]/15 disabled:opacity-60"
                data-testid="submit-inquiry-btn"
              >
                {submitting ? 'Sending...' : 'Request Private Preview'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-[#C8A45D]/70 text-[#C8A45D] hover:bg-[#C8A45D]/10 bg-transparent py-6 transition-all duration-300"
                onClick={() => (window.location.href = 'mailto:house@ikaruslanded.com')}
                data-testid="email-house-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email The House
              </Button>
            </div>
          </form>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
